import { IconButton } from "@circle-react-shared/uikit/HeaderV3/IconButton";
import { Dropdown } from "@circle-react-uikit/Dropdown";
import { useIfSignupEnabled } from "@circle-react/contexts/punditUserContext";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { memberProfileModalPathsWithState } from "@circle-react/helpers/urlHelpers";
import type { PathWithState } from "@circle-react/helpers/urlHelpers";
import { useTanstackRouterQueryParams as useRouterQueryParams } from "@circle-react/hooks/useRouterQueryParams";
import { useLocation } from "@tanstack/react-router";

import { t } from "@/i18n-js/instance";
import { useProfileDrawer } from "@/react/components/Drawers/ProfileDrawer";
import { UserImage } from "@/react/components/SettingsApp/CompassCompat/UserImage";
import { isCommunityAdmin } from "@/react/helpers/communityMemberHelpers";
import {
  anchorClassNames,
  legacyClassNames,
} from "@/react/helpers/cssAnchorHelpers";
import { authPathsWithRedirect } from "@/react/helpers/urlHelpers";

interface ItemLink {
  action?: () => void | Promise<void>;
  label: string;
  to?: string | PathWithState;
}

export const RightActionContent = () => {
  const { currentUser, currentCommunityMember } = usePunditUserContext();
  const location = useLocation({
    select: location => ({
      pathname: location.pathname,
      search: location.searchStr,
    }),
  });
  const ifSignupEnabled = useIfSignupEnabled();
  const { show: showMemberProfileDrawer } = useProfileDrawer();
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const { post_login_redirect: postLoginRedirectUrlParam } =
    useRouterQueryParams();

  const {
    can_access_billing: shouldShowBilling,
    can_access_authentication_page: shouldShowAuthentication,
  } = currentCommunityMember?.policies || {};

  const LOGGED_IN_MENU_ITEM_LINKS: ItemLink[] = [
    {
      action: () => {
        if (!currentCommunityMember) return;
        void showMemberProfileDrawer({
          memberPublicId: currentCommunityMember.public_uid,
        });
      },
      label: t("my_profile"),
    },
    {
      to: memberProfileModalPathsWithState.profile(location),
      label: t("edit_profile"),
    },
    {
      to: memberProfileModalPathsWithState.notifications(location),
      label: t("notification"),
    },
  ];

  if (shouldShowAuthentication) {
    LOGGED_IN_MENU_ITEM_LINKS.push({
      to: memberProfileModalPathsWithState.authentication(location),
      label: t("authentication"),
    });
  }

  if (shouldShowBilling) {
    LOGGED_IN_MENU_ITEM_LINKS.push({
      to: memberProfileModalPathsWithState.billing(location),
      label: t("billing"),
    });
  }

  if (isAdmin) {
    LOGGED_IN_MENU_ITEM_LINKS.push(
      {
        action: () => {
          window.location.href = "/analytics";
        },
        label: t("analytics_title"),
      },
      {
        to: "/settings/audience/manage",
        label: t("audience"),
      },
      {
        to: "/settings",
        label: t("settings_title"),
      },
    );
  }

  if (currentUser && currentCommunityMember) {
    return (
      <Dropdown
        className={classNames(anchorClassNames("user-menu"), "z-[9999]")}
        button={
          <UserImage
            name={currentCommunityMember?.name}
            src={currentCommunityMember?.avatar_url}
            size="8"
          />
        }
        appendTo={document.getElementById("mainHeader") || document.body}
      >
        <div>
          <div className={classNames("bg-secondary -mt-2 mb-1.5 w-full py-1")}>
            <Dropdown.ItemWithLink
              to={`/u/${currentCommunityMember?.public_uid}?show_back_link=true`}
            >
              {currentCommunityMember?.name}
            </Dropdown.ItemWithLink>
          </div>

          {LOGGED_IN_MENU_ITEM_LINKS.map(item => (
            <Dropdown.ItemWithLink
              key={item.label}
              to={item.to}
              onClick={item.action}
            >
              {item.label}
            </Dropdown.ItemWithLink>
          ))}

          <Dropdown.Separator />
          <Dropdown.ItemLink
            onClick={() => (window.location.href = "/users/sign_out")}
          >
            {t("sign_out")}
          </Dropdown.ItemLink>
        </div>
      </Dropdown>
    );
  }

  return (
    <div className={legacyClassNames("user-join__mobile")}>
      <div className={legacyClassNames("user-join__menu")}>
        <Dropdown
          button={
            <IconButton
              name="20-user"
              iconSize={20}
              buttonSize={28}
              ariaLabel={t("log_in")}
              iconClassName="!w-5 !h-5 !text-c-header"
            />
          }
          className="z-[9999]"
          appendTo={document.getElementById("mainHeader") || document.body}
        >
          <Dropdown.ItemLink
            onClick={() =>
              (window.location.href = authPathsWithRedirect.signIn(
                Array(postLoginRedirectUrlParam).join(","),
              ))
            }
          >
            {t("log_in")}
          </Dropdown.ItemLink>
          {ifSignupEnabled(({ communitySignupUrl }) => (
            <Dropdown.ItemLink
              onClick={() => (window.location.href = communitySignupUrl)}
            >
              {t("sign_up")}
            </Dropdown.ItemLink>
          ))}
        </Dropdown>
      </div>
    </div>
  );
};
